/**
 * ריווח בין הדבקים: telling a swallowed word from a skipped one.
 *
 * A fluent reader runs סוּס וְרֹכְבוֹ together, and the recogniser, hearing
 * one stretch of sound, returns one token, ורכבו. The aligner then sees סוס
 * with nothing to match and marks it skipped, which penalises exactly the
 * reader who needs no penalty; Berachot 15b asks that reader to leave a
 * space between the words, not to read them again.
 *
 * The letters cannot tell the two cases apart, because the short word is
 * simply absent from the transcript. The clock can. Speechmatics returns
 * contiguous word timings, so the swallowed word's audio is absorbed into
 * its neighbours: either as silence between them or, more often, as the
 * neighbouring tokens running longer than that reader's own pace would
 * predict. When that unaccounted time is at least what the missing word
 * would have taken, the word was said and glued, not dropped.
 *
 * The yardstick is the reader, not a constant: a slow reader and a fast one
 * are each judged against their own median seconds per syllable in this
 * reading, measured over the words they got right.
 */

const VOWEL_MARKS = /[\u05B1-\u05BB\u05C7]/g;
const SHVA_AT_WORD_START = /^[\u05D0-\u05EA][\u05BC\u05C1\u05C2]*\u05B0/;
const HEBREW_LETTERS = /[\u05D0-\u05EA]/g;

/** Seconds per syllable assumed until the reader has shown their own pace. */
export const DEFAULT_PACE = 0.3;

/** Correct words needed before the reader's own pace replaces the default. */
export const MIN_PACE_SAMPLES = 3;

export interface TimedWord {
  start_time: number;
  end_time: number;
}

/**
 * Syllables in a word, from its vowel points when it has them. A shva at
 * the head of the word is na and carries a syllable of its own. Unpointed
 * text (the Mishna) gets an estimate from its letter count.
 */
export function syllableCount(word: string): number {
  const vowels = (word.match(VOWEL_MARKS) ?? []).length;
  if (vowels > 0) {
    return vowels + (SHVA_AT_WORD_START.test(word) ? 1 : 0);
  }
  const letters = (word.match(HEBREW_LETTERS) ?? []).length;
  return Math.max(1, Math.round(letters / 2.2));
}

/**
 * Median seconds per syllable over correctly read words. Falls back to the
 * default until there are enough samples to trust.
 */
export function readerPace(samples: ReadonlyArray<{ word: string; timing: TimedWord | null }>): number {
  const rates: number[] = [];
  for (const { word, timing } of samples) {
    if (!timing) continue;
    const seconds = timing.end_time - timing.start_time;
    if (!(seconds > 0)) continue;
    rates.push(seconds / syllableCount(word));
  }
  if (rates.length < MIN_PACE_SAMPLES) return DEFAULT_PACE;
  rates.sort((a, b) => a - b);
  const mid = Math.floor(rates.length / 2);
  return rates.length % 2 ? rates[mid] : (rates[mid - 1] + rates[mid]) / 2;
}

export interface CoarticulationCase {
  /** The word the recogniser did not return. */
  skipped: string;
  /** The correctly matched word before it, with its timing. */
  previous: { word: string; timing: TimedWord | null };
  /** The correctly matched word after it, with its timing. */
  next: { word: string; timing: TimedWord | null };
  /**
   * The word after that, when it too was matched. The recogniser's token
   * boundaries are approximate, and a swallowed word's time often lands one
   * token further on: at 15:11 on 28 Sep the missing אֶת showed up as הַזֹּאת
   * running 0.60s for two syllables, with הַשִּׁירָה between them on pace.
   */
  afterNext?: { word: string; timing: TimedWord | null };
  /** The reader's seconds per syllable in this reading. */
  pace: number;
}

/**
 * Was the skipped word said and swallowed into a neighbour?
 *
 * Time available: the gap between the neighbours, plus whatever each
 * neighbour ran over its own expected length. Time needed: the skipped
 * word's syllables at the reader's pace. Without timings on both sides the
 * answer is no, because a guess in the reader's favour here would also be a
 * guess against the teacher's report.
 */
export function isCoarticulated({ skipped, previous, next, afterNext, pace }: CoarticulationCase): boolean {
  if (!previous.timing || !next.timing) return false;

  const gap = Math.max(0, next.timing.start_time - previous.timing.end_time);
  const overrun = (w?: { word: string; timing: TimedWord | null }) =>
    w?.timing
      ? Math.max(0, w.timing.end_time - w.timing.start_time - syllableCount(w.word) * pace)
      : 0;

  const available = gap + overrun(previous) + overrun(next) + overrun(afterNext);
  const needed = syllableCount(skipped) * pace;

  return available >= needed;
}
